import {isFilled} from '@prismicio/client'
import cn from 'classnames'
import type {BigNumberStatsSlice} from 'prismicio-types'
import {DEFAULT_PADDING, PADDING_BOTTOM_CLASS, PADDING_TOP_CLASS} from 'src/common/paddingClasses'

type Props = {
  slice: BigNumberStatsSlice
}

const bgMap: Record<string, string> = {
  'Dark mode': 'bg-green-dark',
  'Light mode grey': 'bg-moss-grey-100',
  'Light mode white': 'bg-moss-white',
}

export const DefaultBigNumberStats = ({slice}: Props) => {
  const p = slice.primary
  const items = slice.items.filter((item) => isFilled.keyText(item.stat_number) || isFilled.keyText(item.stat_label))

  if (items.length === 0) {
    return null
  }

  const isDark = p.background === 'Dark mode'
  const bgClass = bgMap[p.background ?? 'Dark mode'] ?? 'bg-green-dark'

  const paddingTopClass = PADDING_TOP_CLASS[p.padding_top ?? DEFAULT_PADDING] ?? ''
  const paddingBottomClass = PADDING_BOTTOM_CLASS[p.padding_bottom ?? DEFAULT_PADDING] ?? ''
  const colCount = Math.min(items.length, 4)

  const gridColsClass: Record<number, string> = {
    2: 'lg:grid-cols-2',
    3: 'lg:grid-cols-3',
    4: 'lg:grid-cols-4',
  }

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      data-nav-dark={isDark || undefined}
      className={cn(bgClass, paddingTopClass, paddingBottomClass)}
    >
      <div
        className={cn(
          'mx-auto grid max-w-[1200px] grid-cols-2 px-5 md:px-10 xl:px-0',
          gridColsClass[colCount] ?? 'lg:grid-cols-4',
        )}
      >
        {items.map((item, index) => {
          const isFirstInRow = index % 2 === 0
          const isFirstInDesktopRow = index % colCount === 0

          return (
            <div
              key={index}
              className={cn('flex min-h-[260px] flex-col gap-1 px-4 py-8 lg:px-6 lg:py-0', {
                'pl-0 lg:pl-0': isFirstInDesktopRow,
                'max-md:pl-0': isFirstInRow,
                'border-l border-moss-white/10': !isFirstInDesktopRow,
                'max-lg:border-l-0': true,
                'max-lg:border-l max-lg:border-moss-white/10': !isFirstInRow,
                'max-lg:border-t max-lg:border-moss-white/10': index >= 2,
              })}
            >
              {isFilled.keyText(item.stat_number) && (
                <p
                  className={cn(
                    'text-[48px] font-normal leading-none tracking-[-0.48px] md:text-[72px] md:tracking-[-0.72px]',
                    {
                      'text-green-neon': isDark,
                      'text-green-deep': !isDark,
                    },
                  )}
                >
                  {item.stat_number}
                </p>
              )}
              {isFilled.keyText(item.stat_label) && (
                <p className="text-[24px] font-normal leading-tight text-neon-550 md:text-[36px]">{item.stat_label}</p>
              )}
              {isFilled.keyText(item.stat_description) && (
                <p
                  className={cn('mt-auto pt-6 text-[11px] font-semibold uppercase leading-4 tracking-widest', {
                    'text-moss-white': isDark,
                    'text-moss-grey-400': !isDark,
                  })}
                >
                  {item.stat_description}
                </p>
              )}
            </div>
          )
        })}
      </div>
    </section>
  )
}
